import React, { useState, useContext, useEffect } from 'react';
import { Box, Button, TextField, Typography, MenuItem, CircularProgress, Paper, Grid, Dialog, DialogTitle, DialogContent } from '@mui/material';
import API_BASE_URL from '../config';
import { UserContext } from '../UserContext';

const volunteeringTypes = [
  'Environment',
  'Education',
  'Healthcare',
  'Community',
  'Animal Welfare',
  'Arts & Culture',
  'Other',
];

async function fetchWithFallback(endpoint, options) {
  return fetch(`${API_BASE_URL}${endpoint}`, options);
}

export default function VolunteeringService() {
  const [volunteeringType, setVolunteeringType] = useState('');
  const [customType, setCustomType] = useState('');
  const [location, setLocation] = useState('');
  const [targetHours, setTargetHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [plan, setPlan] = useState([]);
  const { user } = useContext(UserContext) || {};

  useEffect(() => {
    // Fetch user's plan on mount
    const fetchPlan = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/plan`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPlan(data);
        }
      } catch (err) {}
    };
    fetchPlan();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setRecommendations([]);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('You must be logged in.');
      const formData = new FormData();
      formData.append('volunteering_type', volunteeringType === 'Other' ? customType : volunteeringType);
      formData.append('location', location);
      formData.append('target_hours', targetHours);
      const response = await fetchWithFallback('/volunteering-recommendations', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get volunteering recommendations');
      }
      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add to Plan handler
  const handleAddToPlan = async (opp) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_id: opp.item_id || opp.name.replace(/\s+/g, '_').toLowerCase(),
          item_type: 'volunteer',
          name: opp.name,
          time_commitment: opp.time_commitment || opp.duration || '',
          details: opp
        })
      });
      const data = await response.json();
      if (data.success) {
        setPlan(plan => [...plan, data.plan_item]);
      } else {
        setError(data.error || 'Failed to add to plan');
      }
    } catch (err) {
      setError('Failed to add to plan');
    }
  };

  // Check if item is already in plan
  const isInPlan = (opp) => plan.some(item => item.item_id === (opp.item_id || opp.name.replace(/\s+/g, '_').toLowerCase()) && item.item_type === 'volunteer');

  return (
    <Box maxWidth="md" mx="auto" py={6} pt={10}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 700, mx: 'auto', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Find Your Perfect Volunteering Opportunity
        </Typography>
        <Typography align="center" color="textSecondary" sx={{ mb: 3 }}>
          Tell us what kind of volunteering you want to do, your location, and your target hours!
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Type of Volunteering"
                value={volunteeringType}
                onChange={e => setVolunteeringType(e.target.value)}
                fullWidth
                required
              >
                {volunteeringTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {volunteeringType === 'Other' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Type"
                  value={customType}
                  onChange={e => setCustomType(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location"
                value={location}
                onChange={e => setLocation(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Hours per Month"
                type="number"
                value={targetHours}
                onChange={e => setTargetHours(e.target.value)}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
                {loading ? <CircularProgress size={24} /> : 'Find Opportunities'}
              </Button>
            </Grid>
          </Grid>
        </form>
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Paper>
      {recommendations.length > 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} align="center" gutterBottom>
            Recommended Volunteering Opportunities
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {recommendations.map((opp, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, cursor: 'pointer' }}>
                  <Typography variant="h6" fontWeight={600}>{opp.name}</Typography>
                  <Typography color="textSecondary">{opp.location} | {opp.duration}</Typography>
                  <Typography sx={{ mt: 1 }}>{opp.description}</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    color={isInPlan(opp) ? 'success' : 'primary'}
                    sx={{ mt: 2, mr: 2 }}
                    onClick={() => handleAddToPlan(opp)}
                    disabled={isInPlan(opp)}
                  >
                    {isInPlan(opp) ? 'Added' : 'Add to Plan'}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setSelectedOpportunity(opp)} sx={{ mt: 2 }}>
                    Learn More
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Dialog open={!!selectedOpportunity} onClose={() => setSelectedOpportunity(null)} maxWidth="sm" fullWidth>
        {selectedOpportunity && (
          <>
            <DialogTitle>{selectedOpportunity.name}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                {selectedOpportunity.type} | {selectedOpportunity.location} | {selectedOpportunity.difficulty}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedOpportunity.description}</Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Duration:</strong> {selectedOpportunity.duration}<br/>
                <strong>Eligibility:</strong> {selectedOpportunity.eligibility}<br/>
                <strong>Cost:</strong> {selectedOpportunity.cost}<br/>
                <strong>Deadline:</strong> {selectedOpportunity.application_deadline}<br/>
                <strong>Benefits:</strong> {selectedOpportunity.benefits}<br/>
                {selectedOpportunity.website && (
                  <span>
                    <strong>Website:</strong> <a href={selectedOpportunity.website} target="_blank" rel="noopener noreferrer">{selectedOpportunity.website}</a><br/>
                  </span>
                )}
                <strong>Contact:</strong> {selectedOpportunity.contact_info}
              </Typography>
            </DialogContent>
            <Box display="flex" justifyContent="flex-end" p={2}>
              <Button onClick={() => setSelectedOpportunity(null)} variant="contained">Close</Button>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
} 